/**
 * AuthenticatedApp Component
 * 
 * Main authenticated application with dashboard layout, sidebar navigation,
 * and user profile display. Only shown to authenticated users.
 * Supports multi-group access for group-admin users.
 */

import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Phone, BarChart3, Settings as SettingsIcon, CreditCard, Download, LogOut, PhoneOutgoing, Activity } from "lucide-react";
import SplashScreen from './components/layout/SplashScreen';
import CallsPage from './pages/CallsPage';
import AnalyticsPage from './pages/AnalyticsPage';
import SettingsPage from './pages/SettingsPage';
import BillingPage from './pages/BillingPage';
import OutboundPage from './pages/OutboundPage';
import UsagePage from './pages/UsagePage';
import { Button } from './components/ui/button';
import { Separator } from './components/ui/separator';
import { setAuth0TokenGetter } from './api/apiClient';
import yapnLogo from './assets/yapn-logo.png';
import ExportReportModal from './components/common/ExportReportModal';
import ErrorBoundary from './components/common/ErrorBoundary';
import { useSettings } from './contexts/SettingsContext';
import useRestaurants from './hooks/useRestaurants';
import { useUser } from './hooks/useUser';

function DashboardLayout({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth0();
  const [showExportModal, setShowExportModal] = useState(false);
  const { 
    selectedLocationId, 
    setSelectedLocationId,
    setAvailableGroups
  } = useSettings();
  const { groupIds, isHubkomClient: isHubkomFromAuth } = useUser();
  const { restaurants, isLoading: isLoadingRestaurants } = useRestaurants();

  // Initialize available groups from user's groupIds (used by billing and other group-specific features)
  useEffect(() => {
    if (groupIds && groupIds.length > 0) {
      setAvailableGroups(groupIds);
    }
  }, [groupIds, setAvailableGroups]);

  // Detect Hubkom from Auth0 metadata OR selected restaurant record
  const selectedRestaurantForHubkom = restaurants?.find(r => r.locationId === selectedLocationId);
  const isHubkomClient = isHubkomFromAuth || selectedRestaurantForHubkom?.isHubkomClient === true;

  // Billing or Usage nav item based on client type
  const billingNavItem = isHubkomClient
    ? { id: "usage", label: "Usage", icon: Activity, path: "/usage" }
    : { id: "billing", label: "Billing", icon: CreditCard, path: "/billing" };

  // Base navigation items
  const baseNavItems = [
    { id: "calls", label: "Calls", icon: Phone, path: "/" },
    { id: "analytics", label: "Analytics", icon: BarChart3, path: "/analytics" },
    billingNavItem,
    { id: "settings", label: "Settings", icon: SettingsIcon, path: "/settings" }
  ];

  // Check if selected restaurant has outbound enabled
  const selectedRestaurant = restaurants?.find(r => r.locationId === selectedLocationId);
  const hasOutbound = selectedRestaurant?.outbound === true;

  // Build navigation items - add Outbound if enabled for selected location
  const navItems = hasOutbound
    ? [
        { id: "calls", label: "Calls", icon: Phone, path: "/" },
        { id: "analytics", label: "Analytics", icon: BarChart3, path: "/analytics" },
        { id: "outbound", label: "Campaigns", icon: PhoneOutgoing, path: "/outbound" },
        billingNavItem,
        { id: "settings", label: "Settings", icon: SettingsIcon, path: "/settings" }
      ]
    : baseNavItems;

  const currentPath = location.pathname;

  const handleLogout = () => {
    logout({ 
      logoutParams: { 
        returnTo: window.location.origin 
      } 
    });
  };

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full bg-gray-900 border-r border-gray-800 w-64 z-40">
        <div className="p-6">
          <div className="mb-8">
            <img src={yapnLogo} alt="YAPN" className="h-16 w-auto" />
          </div>

          {/* Location Selector */}
          <div className="mb-6">
            <label className="block text-xs font-medium text-gray-400 mb-2 px-1">
              Location Name
            </label>
            <select
              value={selectedLocationId || 'all'}
              onChange={(e) => setSelectedLocationId(e.target.value)}
              disabled={isLoadingRestaurants}
              className="w-full px-3 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm hover:bg-gray-750 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="all">All Locations</option>
              {restaurants
                ?.slice()
                .sort((a, b) => {
                  const nameA = (a.organization || a.restaurantName || a.name || `Location ${a.locationId}`).toLowerCase();
                  const nameB = (b.organization || b.restaurantName || b.name || `Location ${b.locationId}`).toLowerCase();
                  return nameA.localeCompare(nameB);
                })
                .map((restaurant) => (
                  <option key={restaurant.locationId} value={restaurant.locationId}>
                    {restaurant.organization || restaurant.restaurantName || restaurant.name || `Location ${restaurant.locationId}`}
                  </option>
                ))}
            </select>
          </div>

          <nav className="space-y-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = currentPath === item.path || 
                (item.path === "/" && currentPath === "/");
              
              return (
                <button
                  key={item.id}
                  onClick={() => navigate(item.path)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive
                      ? "bg-gradient-to-r from-[#3AD0E6] to-[#3630A4] text-white"
                      : "text-gray-400 hover:bg-gray-800 hover:text-white"
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </nav>

          <Separator className="bg-gray-800 my-6" />

          <Button 
            variant="outline" 
            className="w-full bg-gray-800 border-gray-700 text-white hover:bg-gray-700"
            onClick={() => setShowExportModal(true)}
          >
            <Download className="w-4 h-4 mr-2" />
            Export Report
          </Button>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-gray-900 to-transparent">
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
            <div className="flex items-center gap-3 mb-3">
              {user?.picture && (
                <img 
                  src={user.picture} 
                  alt={user.name || 'User'} 
                  className="w-10 h-10 rounded-full"
                />
              )}
              <div className="flex-1 min-w-0">
                <p className="text-gray-400 text-xs mb-1">Account</p>
                <p className="text-white text-sm truncate">{user?.name || 'User'}</p>
                <p className="text-gray-500 text-xs truncate">{user?.email}</p>
              </div>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleLogout}
              className="w-full text-gray-400 hover:text-white hover:bg-gray-700"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="ml-64 min-h-screen">
        <ErrorBoundary>
          {children}
        </ErrorBoundary>
      </main>

      {/* Export Report Modal */}
      <ExportReportModal 
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
      />
    </div>
  );
}

function ProtectedRoutes() {
  const { isHubkomClient: isHubkomFromAuth2 } = useUser();
  const { restaurants: allRestaurants } = useRestaurants();
  const { selectedLocationId: selectedLocId } = useSettings();
  const selectedLoc = allRestaurants?.find(r => r.locationId === selectedLocId);
  const isHubkomClient = isHubkomFromAuth2 || selectedLoc?.isHubkomClient === true;

  return (
    <Routes>
      {/* Main dashboard route - Overview/Calls */}
      <Route path="/" element={<CallsPage />} />
      <Route path="/calls" element={<CallsPage />} />

      {/* Analytics route */}
      <Route path="/analytics" element={<AnalyticsPage />} />

      {/* Outbound route - only shown for locations with outbound enabled */}
      <Route path="/outbound" element={<OutboundPage />} />

      {/* Usage route for Hubkom clients */}
      <Route path="/usage" element={<UsagePage />} />

      {/* Billing route - redirects to usage for Hubkom clients */}
      <Route path="/billing" element={isHubkomClient ? <Navigate to="/usage" replace /> : <BillingPage />} />

      {/* Settings route */}
      <Route path="/settings" element={<SettingsPage />} />

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function AuthenticatedApp() {
  const [showSplash, setShowSplash] = useState(true);
  const { getAccessTokenSilently } = useAuth0();

  // Set up Auth0 token getter for API client
  useEffect(() => {
    setAuth0TokenGetter(getAccessTokenSilently);
  }, [getAccessTokenSilently]);

  // Check if splash screen has been shown before
  useEffect(() => {
    const hasSeenSplash = sessionStorage.getItem('yapn_splash_shown');
    if (hasSeenSplash) {
      setShowSplash(false);
    }
  }, []);

  const handleSplashComplete = () => {
    sessionStorage.setItem('yapn_splash_shown', 'true');
    setShowSplash(false);
  };

  return (
    <div className="dark">
      {showSplash && <SplashScreen onComplete={handleSplashComplete} />}
      
      <Router>
        <DashboardLayout>
          <ProtectedRoutes />
        </DashboardLayout>
      </Router>
    </div>
  );
}

export default AuthenticatedApp;
